<script lang="ts" setup>
import {
  ref,
  useDialog,
  useRoute,
  computed,
  useDrawer,
  useAuth,
  useUser,
  onNuxtReady,
  useHeaderStyle,
} from "#imports";
import { useMediaQuery } from "@vueuse/core";
import MPAuth from "../dialogs/MPAuth/MPAuth.vue";
import MPSearchInline from "../forms/MPSearchInline.vue";
import MPPricing from "../dialogs/MPPricing.vue";
import MPButton from "../forms/MPButton.vue";
import MPImg from "../widgets/MPImg.vue";

// init
const route = useRoute();
const { toggleDrawer } = useDrawer();
const { headerStyle } = useHeaderStyle();

const isLessThanSm = useMediaQuery("(max-width: 920px)");
// Stop flash of mobile menu and wait for app to mount
const showMenu = ref(false);
onNuxtReady(() => {
  showMenu.value = true;
});

// load
const authStore = useAuth();
const userStore = useUser();

const isStudio = computed(
  () => route.path.startsWith("/studio") || route.path.startsWith("/admin"),
);

// Premium Header link
const { openDialog } = useDialog();
const handleGetPremiumClick = () => {
  openDialog(MPPricing, {});
};
</script>
<template>
  <header v-if="!isStudio" :class="[headerStyle]">
    <NuxtLinkLocale to="/" class="logo">
      <img src="/logos/logo-circle-indigo.svg" alt="Mappr" />
    </NuxtLinkLocale>
    <div v-if="showMenu" class="menu">
      <div class="main-menu">
        <NuxtLinkLocale
          to="/explore"
          exactActiveClass="isActive"
          :class="{
            isActive:
              route.path.startsWith('/explore') ||
              route.path.startsWith('/maps'),
          }"
        >
          <i class="ri-compass-3-line" />
          <span>Explore</span>
        </NuxtLinkLocale>
        <NuxtLinkLocale
          to="/discover"
          exactActiveClass="isActive"
          :class="{ isActive: route.path.startsWith('/discover') }"
        >
          <i class="ri-compass-discover-line" />
          <span>Discover</span>
        </NuxtLinkLocale>
        <a
          v-if="isLessThanSm"
          @click="openDialog(MPSearchInline, {})"
          exactActiveClass="isActive"
        >
          <i class="ri-search-2-line" />
          <span>Search</span>
        </a>
        <NuxtLinkLocale
          to="/atlas"
          exactActiveClass="isActive"
          :class="{ isActive: route.path.startsWith('/atlas') }"
        >
          <i class="ri-earth-line" />
          <span>My&nbsp;Atlas</span>
        </NuxtLinkLocale>
      </div>
      <MPSearchInline
        v-if="!isLessThanSm"
        placeholder="Search..."
        class="search-input"
      />
      <MPButton
        v-if="!isLessThanSm && !authStore.isLoggedIn"
        label="Sign up for free"
        rounded
        size="large"
        version="secondary"
        class="sign-up"
        @click="openDialog(MPAuth, { startOn: 'signup' })"
      />
      <MPButton
        v-if="!isLessThanSm && authStore.isLoggedIn && !userStore.isPremium"
        label="Get Premium"
        version="secondary"
        rounded
        size="large"
        class="get-premium"
        @click="handleGetPremiumClick()"
      />
      <MPButton
        v-if="!isLessThanSm && !authStore.isLoggedIn"
        label="Login"
        size="large"
        rounded
        class="login"
        @click="openDialog(MPAuth, { startOn: 'login', title: 'Join Mappr' })"
        style="margin-left: 0.5rem"
      />
      <button
        v-if="isLessThanSm"
        label="Login"
        aria-label="Login"
        class="user-menu"
        @click="
          authStore.isLoggedIn
            ? toggleDrawer()
            : openDialog(MPAuth, { startOn: 'login', title: 'Join Mappr' })
        "
      >
        <i class="ri-user-line" /><span>Profile</span>
      </button>
      <button
        v-if="authStore.isLoggedIn && !isLessThanSm"
        :aria-label="authStore.isLoggedIn ? 'Login' : 'Profile'"
        class="profile-link user-menu"
        @click="toggleDrawer"
      >
        <MPImg
          v-if="userStore.user.profile_image_url"
          :width="90"
          :height="90"
          :src="userStore.user.profile_image_url"
          class="user-dropdown-trigger-image"
        />
        <i v-else class="ri-user-line" />
      </button>
    </div>
  </header>
</template>

<style lang="postcss" scoped>
@import "@/assets/css/breakpoints.pcss";
@import "@/assets/css/layouts.pcss";

header {
  padding: 1rem;
  transition: opacity 150ms ease-in-out;
  transition: background-color 250ms ease-in-out;
  pointer-events: none;
  z-index: 7000;
  @mixin screen md {
    display: flex;
    position: absolute;
    top: 0;
    left: 0;
    min-width: 100vw;
    @mixin layout-wrapper-wider;
    padding: 1rem 1rem 0;
    gap: 0.75rem;
    justify-content: space-between;
  }

  @mixin screen lg {
    @mixin layout-wrapper-full;
  }
}

.user-menu {
  background-color: transparent;
  border: 0;
  color: inherit;
  padding: 1rem;
  cursor: pointer;
  i {
    position: relative;
    top: -0.1rem;
    font-size: 1.65rem !important;
  }
}

/*======
Logo
======*/
.logo {
  max-width: 2.75rem;
  min-height: 2.75rem;
  pointer-events: all;
  img {
    position: fixed;
    top: 0.75rem;
    left: 0.75rem;
    z-index: 8000;
    @mixin screen md {
      position: relative;
      top: auto;
      left: auto;
    }
  }
}

.logo {
  width: 2.75rem;
  height: 2.75rem;
  border-radius: 100%;
  display: block;
  z-index: 1100;
}

/*
======
Search
======
*/
.search-input {
  @mixin screen md {
    display: none;
  }
}

/*
======
Menu
======
*/
.main-menu {
  background-color: transparent;
  display: flex;
  pointer-events: all;
  @mixin screen md {
    position: absolute;
    top: 0.95rem;
    right: 50%;
    transform: translateX(50%);
    padding: 0.45rem 0.5rem 0.4rem;
    background-color: white;
    box-shadow: var(--shadow-base);
    color: var(--color-slate-700);
    border-radius: 2rem;
    a i {
      display: none;
    }
  }
}

.menu {
  z-index: 8000;
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: 300ms background-color ease-in-out;
  background-color: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(10px);
  color: var(--color-slate-600);
  box-shadow: var(--shadow-md);
  pointer-events: all;
  @mixin screen md {
    width: auto;
    backdrop-filter: none;
    position: unset;
    padding: 0;
    background-color: transparent;
    box-shadow: none;
    color: var(--color-slate-700);
  }
}

.menu a {
  position: relative;
  padding: 1rem 1.25rem 1.25rem;
  display: grid;
  align-items: center;
  justify-content: center;
  text-align: center;
  transition: 250ms all ease-in-out;
  cursor: pointer;

  @mixin screen md {
    padding: 0.5rem 1rem;
    gap: 0.25rem;
    border-radius: 1rem;
  }
}

.menu i {
  font-size: 1.75rem;
}

.menu span {
  position: absolute;
  bottom: 0.5rem;
  left: 50%;
  transform: translateX(-50%);
  transition: 50ms all ease-in-out;
  transition-delay: 70ms;
  opacity: 0;
  font-size: 0.8rem;
  @mixin screen md {
    position: relative;
    bottom: -0.045rem;
    opacity: 1;
    font-size: 0.95rem;
  }
}

.menu .profile-link {
  cursor: pointer;
  @mixin screen md {
    padding: 0.15rem;
    gap: 0;
    box-shadow: var(--shadow-base);
    background-color: white;
    border-radius: 100%;
    margin-left: 0.5rem;
  }
}

.sign-up,
.get-premium {
  background: white !important;
}

.sign-up,
.get-premium,
.login {
  @mixin screen md {
    box-shadow: var(--shadow-base);
    border: 0 !important;
  }
}

.menu .profile-link img {
  width: 2.75rem;
  height: 2.75rem;
  object-fit: cover;
  border-radius: 100%;
  margin-left: 0;
}

.menu .profile-link span {
  @mixin screen md {
    display: none;
  }
}

.menu .search-input {
  display: none;
  @mixin screen md {
    display: inline;
    margin: 0 0.5rem;
    &:deep(input) {
      border-radius: 2rem;
      border: none;
      background-color: white;
      color: var(--color-slate-700);
      padding: 0.825rem 1.45rem;
      width: 1rem;
      transition: width 200ms ease-in-out;
      box-shadow: var(--shadow-base);
      cursor: pointer;
    }
    &:deep(input.active) {
      width: 15rem;
      padding: 0.85rem 2.5rem;
      transition: width 200ms ease-in-out;
      cursor: text;
    }
    &:deep(input::placeholder) {
      color: transparent;
      transition: color 200ms ease-in-out;
      transition-delay: 250ms;
    }
    &:deep(input::placeholder) {
      color: var(--color-slate-700);
      transition: color 250ms ease-in-out;
      transition-delay: 200ms;
    }
    &:deep(::before) {
      color: var(--color-slate-700);
      pointer-events: none;
      left: 0.7rem;
      top: 0.625rem;
      font-size: 1.5rem;
    }
  }
}

.menu .premium-link {
  cursor: pointer;
  display: none;
  @mixin screen md {
    display: grid;
  }
}

.menu a.isActive {
  transition: 50ms all ease-in-out;
  color: var(--color-rose-400);
  @mixin screen md {
    background-color: var(--color-slate-100);
    color: var(--color-slate-600);
    padding: 0.5rem 1.5rem 0.5rem 1.5rem;
  }
}

.menu a.isActive i {
  top: -0.25rem;
  position: relative;
  @mixin screen md {
    top: 0;
  }
}

.menu a.isActive span {
  transition: 50ms all ease-in-out;
  transition-delay: 70ms;
  opacity: 1;
}

/*
======
State
======
*/

header.transparent .menu {
  background-color: transparent;
  color: var(--color-slate-50);
  @mixin screen md {
    color: inherit;
  }
}

header.inverted .menu {
  background-color: transparent;
  color: var(--color-slate-50);
}

header.inverted .logo:deep(svg path) {
  fill: var(--color-slate-50);
}

header.inverted .premium-link {
  color: var(--color-slate-600);
}

header.background {
  background-color: white;
  color: var(--color-slate-700);
  box-shadow: var(--shadow-sm);
  transition: background-color 50ms ease-in-out;
}
</style>
